import type { Dispatch, SetStateAction } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import type { IDistrict } from 'features/Address'
import type { AddressOptionKey, BranchForForm, Field, OptionProps } from 'types'

import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dropdown, Loader } from 'components'
import * as utils from 'helpers/getAddress'

interface IInputDistrict {
  districts: IDistrict[]
  isDistrictsLoading: boolean
  isDistrictChecking: boolean
  setIsDistrictChecking: Dispatch<SetStateAction<boolean>>
  field: ControllerRenderProps
  handleOptions: (
    districts: IDistrict[],
    name: Field,
  ) => OptionProps<AddressOptionKey>[]
  handleSelected: <T extends { [label: string]: string; id: string }>(
    selectedValue: T,
    name: Field,
  ) => OptionProps<AddressOptionKey> | undefined
  getSubDistricts: (districtId: string) => void
  emptyId: { id: string }
}

export const InputDistrict: React.FC<IInputDistrict> = ({
  districts,
  isDistrictsLoading,
  isDistrictChecking,
  setIsDistrictChecking,
  field,
  emptyId,
  handleOptions,
  handleSelected,
  getSubDistricts,
}) => {
  const { errors, setValue, watch, trigger } = useFormContext<BranchForForm>()

  const { isSameAsCompany, province, district } = watch([
    'isSameAsCompany',
    'province',
    'district',
  ])
  const { t } = useTranslation(['common'])

  const onChange = (item: OptionProps<AddressOptionKey>) => {
    field.onChange(item.key)
    setValue('subDistrict', emptyId)
    setValue('postalCode', '')
    getSubDistricts(item.key.id)
  }

  const onBlur = async () => {
    setIsDistrictChecking(true)
    await trigger('district')
    setIsDistrictChecking(false)
  }

  const isError = utils.handleIsError(
    isSameAsCompany,
    district.id,
    Boolean(errors.district),
  )

  return (
    <Dropdown
      placeholder={t('common:address.district.placeholder')}
      options={handleOptions(districts, 'district')}
      selected={handleSelected(field.value, 'district')}
      disabled={Boolean(!province.id) || isSameAsCompany}
      onChange={onChange}
      onBlur={onBlur}
      isError={isError}
      withIcon={(isDistrictsLoading || isDistrictChecking) && <Loader />}
      fullWidth
      ref={field.ref}
    />
  )
}
